import React, { useState, useEffect, useContext } from "react";
import { Spinner, Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faTools,
} from "@fortawesome/free-solid-svg-icons";
import api from "../../../axiosConfig";
import { AuthContext } from "../../../contexts/AuthContext";
import { popup } from "../../common/popup";
import "./css/Toolstab.css";

// Nama sektor di tabel `tools` tidak selalu sama persis dengan nama di master
// sektor (mis. "LINE WIRE 3" vs "LINE 3", "U-LINE 1" vs "ULINE 1",
// "PEMILIN" vs "LINE PEMILIN"). Normalkan dulu agar bisa dicocokkan.
const normalizeSector = (s) =>
  (s || "")
    .toUpperCase()
    .replace(/WIRE/g, "") // "LINE WIRE 3" -> "LINE  3"
    .replace(/-/g, "") // "U-LINE 1" -> "ULINE 1"
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^LINE\s+/, "") // "LINE 3" -> "3", "LINE PEMILIN" -> "PEMILIN"
    .trim();

const ToolsTab = () => {
  const { currentUser } = useContext(AuthContext);
  // Bila user ditautkan ke sektor/line tertentu, kunci tools ke sektor itu
  const lockedSectorName = currentUser?.sector_id
    ? currentUser?.sector_name || null
    : null;
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportingId, setReportingId] = useState(null); // ⬅️ PENTING
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [search, setSearch] = useState("");
  const [selectedSector, setSelectedSector] = useState("ALL");

  /* =========================
     FETCH TOOLS
  ========================= */
  const fetchTools = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const res = await api.get("/maintenance/tools/list", {
        headers: { "x-auth-token": token },
      });

      setTools(res.data || []);
    } catch (err) {
      console.error("❌ Error ambil tools:", err);
      setTools([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredTools = tools.filter((tool) => {
    const keyword = search.toLowerCase();

    const matchSearch =
      tool.spec?.toLowerCase().includes(keyword) ||
      tool.part_name?.toLowerCase().includes(keyword);

    const matchCategory =
      selectedCategory === "ALL" ||
      tool.category?.toUpperCase() === selectedCategory;

    const matchStatus =
      selectedStatus === "ALL" || tool.tool_status === selectedStatus;

    const matchSector =
      selectedSector === "ALL" || tool.sector_name === selectedSector;

    // Kunci ke sektor user (bila ada). Cocokkan via normalisasi nama sektor.
    const matchLockedSector =
      !lockedSectorName ||
      normalizeSector(tool.sector_name) === normalizeSector(lockedSectorName);

    return (
      matchSearch &&
      matchCategory &&
      matchStatus &&
      matchSector &&
      matchLockedSector
    );
  });

  useEffect(() => {
    fetchTools();
  }, []);

  /* =========================
     HELPERS
  ========================= */
  const formatDate = (date) => {
    if (!date) return "Belum Pernah";

    const d = new Date(date);

    return d.toLocaleString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false, // format 24 jam
    });
  };

  const translateStatus = (status) => {
    switch (status) {
      case "OK":
        return "OK";
      case "BUTUH_DIPERBAIKI":
        return "Menunggu Perbaikan";
      case "SEDANG_DIPERBAIKI":
        return "Sedang Diperbaiki";
      case "NG":
        return "NG";
      default:
        return "-";
    }
  };

  /* =========================
     REPORT ISSUE
  ========================= */
  const handleReportIssue = async (toolId) => {
    const issue = prompt("Jelaskan masalah tools:");
    if (!issue) return;

    try {
      setReportingId(toolId); // 🔒 kunci button

      const token = localStorage.getItem("token");

      await api.post(
        `/maintenance/tools/report/${toolId}`,
        { issue },
        { headers: { "x-auth-token": token } },
      );

      popup.success("Laporan berhasil dikirim");

      setTools((prev) =>
        prev.map((tool) =>
          tool.id === toolId
            ? {
                ...tool,
                tool_status: "BUTUH_DIPERBAIKI", // ✅ BENAR
                last_maintenance: new Date(),
              }
            : tool,
        ),
      );
    } catch (err) {
      console.error("❌ Report tool error:", err);
      popup.error("Gagal melaporkan masalah");
    } finally {
      setReportingId(null); // 🔓 buka kunci
    }
  };

  /* =========================
     RENDER
  ========================= */
  return (
    <div className="container-fluid tools-page p-2">
      <div className="d-flex gap-2 mb-3 justify-content-end">
        <div col={7} className="d-flex gap-2 align-items-center">
          {lockedSectorName ? (
            <>
              <span className="fw-bold">Line:</span>
              <span
                className="badge bg-primary"
                style={{ fontSize: "14px", padding: "8px 12px" }}
              >
                {lockedSectorName}
              </span>
            </>
          ) : (
            <Form.Select
              size="sm"
              value={selectedSector}
              onChange={(e) => setSelectedSector(e.target.value)}
              style={{ maxWidth: "180px", height: "35px" }}
            >
              <option value="ALL">All Sector</option>
              {[...new Set(tools.map((t) => t.sector_name).filter(Boolean))].map(
                (sec) => (
                  <option key={sec} value={sec}>
                    {sec}
                  </option>
                ),
              )}
            </Form.Select>
          )}

          <Form.Select
            size="sm"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{ maxWidth: "220px" }}
          >
            <option value="ALL">All Kategori</option>
            <option value="JIG">JIG</option>
            <option value="NOZEL">NOZEL</option>
            <option value="MOULDING">MOULDING</option>
            <option value="DIES">DIES</option>
            <option value="PIN">PIN</option>
            <option value="HOLE">HOLE</option>
            <option value="BRADING">BRADING</option>
            <option value="PEMILIN">PEMILIN</option>
          </Form.Select>
        </div>
        <div col={5} className="d-flex gap-2">
          <Form.Select
            size="sm"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{ maxWidth: "170px" }}
          >
            <option value="ALL">All Status</option>
            <option value="OK">OK</option>
            <option value="BUTUH_DIPERBAIKI">Menunggu Perbaikan</option>
            <option value="SEDANG_DIPERBAIKI">Sedang Diperbaiki</option>
            <option value="NG">NG</option>
          </Form.Select>

          <Form.Control
            size="sm"
            placeholder="🔍 Cari..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: "250px" }}
          />
        </div>
      </div>
      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" />
        </div>
      ) : tools.length === 0 ? (
        <div className="text-center p-5">
          <FontAwesomeIcon icon={faTools} size="3x" className="mb-3" />
          <h5>Tidak ada tools</h5>
        </div>
      ) : (
        <div
          className="table-wrapper"
          style={{
            width: "100%",
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: "80vh",
          }}
        >
          <table className="preview-table">
            <thead className="table-light text-center">
              <tr>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>No</th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>Sector</th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Kode / Spec
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>Nama</th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>Status</th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Maintenance Terakhir
                </th>
                <th style={{ background: "#1e3a8a", color: "#fff" }}>
                  Tindakan
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTools.map((tool, index) => {
                const status = tool.tool_status; // 🔥 SATU SUMBER KEBENARAN
                const isDisabled = status !== "OK" || reportingId === tool.id;
                return (
                  <tr key={tool.id}>
                    <td className="text-center">{index + 1}</td>
                    <td className="text-center">{tool.sector_name || "-"}</td>
                    <td className="cell-left">{tool.spec || "-"}</td>
                    <td className="cell-left">{tool.part_name}</td>

                    <td className="text-center">
                      <span
                        className={`status-badge status-${status?.toLowerCase()}`}
                      >
                        {translateStatus(status)}
                      </span>
                    </td>

                    <td className="text-center">
                      {formatDate(tool.last_maintenance)}
                    </td>

                    <td className="text-center">
                      <Button
                        size="sm"
                        variant="warning"
                        disabled={isDisabled}
                        onClick={() => handleReportIssue(tool.id)}
                        title={
                          status === "NG"
                            ? "Tools sudah rusak dan tidak bisa dilaporkan"
                            : status === "SEDANG_DIPERBAIKI"
                              ? "Tools sedang diperbaiki"
                              : "Laporkan masalah"
                        }
                      >
                        {reportingId === tool.id ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            Mengirim...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon={faExclamationTriangle}
                              className="me-1"
                            />
                            Laporkan
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ToolsTab;
